<template>
  <div
    class="autocomplete"
    :class="{ error: error, 'not-empty': notEmpty, open: isOpened && !sameWindow }"
  >
    <modal-header-white
      v-if="isMobile && isOpened"
      :device-type="'mobile'"
      :label="mobileTitle"
      @closed="close"
    />
    <multiselect
      v-bind="bindGroup"
      :id="field"
      :value="selectedItem"
      :options="itemList"
      :searchable="true"
      track-by="value"
      label="title"
      :close-on-select="true"
      :placeholder="placeholder"
      :allow-empty="false"
      :options-limit="250"
      :show-no-results="false"
      :show-no-options="false"
      :show-labels="true"
      :max-height="275"
      :open-direction="openDirection"
      select-label=""
      selected-label=""
      deselect-label=""
      :clear-on-select="false"
      :preserve-search="false"
      :internal-seach="false"
      @open="setOpened"
      @close="setClosed"
      @select="onChange"
      @search-change="onSearch"
    >
      <template #caret="{ toggle }">
        <label
          class="label"
          :for="field"
        >
          {{ label }}
        </label>
        <span
          class="arrow"
          @mousedown.prevent.stop="toggle"
        />
      </template>

      <template
        slot="singleLabel"
        slot-scope="{ option }"
      >
        <div class="selected">{{ option.title }}</div>
      </template>

      <template
        slot="option"
        slot-scope="props"
      >
        <div
          v-if="props.option.$groupLabel"
          class="option__group-title"
        >
          {{ props.option.$groupLabel }}
        </div>
        <div v-else class="option__title">{{ props.option.title }}</div>
      </template>
    </multiselect>
    <errors
      v-if="error"
      :errors="[errorMsg]"
    />
  </div>
</template>

<script>
  const Multiselect = () =>
    import(/* webpackChunkName: "vue-multiselect" */ 'vue-multiselect')
  const ModalHeaderWhite = () =>
    import(
      /* webpackChunkName: "modal-header-white" */ 'components/Modal/ModalHeaderWhite'
    )
  const Errors = () =>
    import(/* webpackChunkName: "element-errors" */ 'components/Elements/Errors')

  export default {
    name: 'ElementsAutocomplete',
    components: {
      Multiselect,
      ModalHeaderWhite,
      Errors
    },
    props: {
      error: {
        type: Boolean,
        required: false,
        default: false
      },
      errorMsg: {
        type: String,
        required: false,
        default: ''
      },
      field: {
        type: String,
        required: true
      },
      label: {
        type: String,
        default: '',
        required: false
      },
      items: {
        type: Array,
        default: () => []
      },
      selectedItem: {
        type: Object,
        default: () => {}
      },
      mobileTitle: {
        type: String,
        default: '',
        required: false
      },
      splitTitle: {
        type: Boolean,
        default: false
      },
      sameWindow: {
        type: Boolean,
        default: false
      },
      ignoreListChangeEvent: {
        type: Boolean,
        default: false
      },
      hasGroups: {
        type: Boolean,
        default: false
      },
      placeholder: {
        type: String,
        required: false,
        default: ''
      },
      openDirection: {
        type: String,
        required: false,
        default: 'bottom'
      }
    },
    data() {
      return {
        isOpened: false,
        itemList: this.items,
        bindGroup: !this.hasGroups ? {} : { 'group-label': 'title', 'group-values': 'items' }
      }
    },
    computed: {
      isMobile() {
        return this.$store.getters['device/isMobile']
      },
      notEmpty() {
        return Object.keys(this.selectedItem).length > 0
      }
    },
    watch: {
      items(val) {
        this.itemList = val
      }
    },
    methods: {
      toggleStatus() {
        if (this.isOpened) {
          this.setClosed()
        } else {
          this.setOpened()
        }
      },
      setOpened() {
        this.isOpened = true
      },
      setClosed() {
        this.isOpened = false
      },
      onChange(value) {
        this.$emit('change', value)
      },
      onSearch(value) {
        let filtered

        if (value !== '') {
          filtered = this.items.filter((data) => {
            return data.title.toLowerCase().startsWith(value.toLowerCase()) > 0
          })
        } else {
          filtered = this.items
        }

        this.itemList = filtered
        this.$emit('search', value)
      },
      close() {
        this.$emit('close')
      }
    }
  }
</script>

<style scoped>
  @import '~assets/styles/vue-multiselect.css';
</style>

<style
  lang="scss"
  scoped
  rel="stylesheet/scss"
>
  @import '~assets/styles/common.scss';

  .autocomplete {
    &.open {
      @include bp-mobile {
        display: block;
        width: 100%;
        position: fixed;
        background: white;
        height: calc(100vh - 55px);
        top: 55px;
        left: 0;
        z-index: 4;
        overflow-y: scroll;

        .arrow {
          display: none;
        }
      }
    }

    &.error {
      margin-bottom: 16px;

      .multiselect {
        margin-bottom: 12px;
      }

      ::v-deep {
        .multiselect__tags {
          border-color: $red-dark;
        }
      }
    }

    &.not-empty {
      .multiselect {
        .label {
          position: absolute;
          transition: 0.2s ease all;
          @include text-overflow();
          pointer-events: none;
          color: $blue-light;
          top: 7px;
          font-size: em(10);
          letter-spacing: 0;
          line-height: 14px;
        }
      }
    }
  }

  .multiselect,
  .multiselect__input,
  .multiselect__single {
    min-height: 56px;
    margin-bottom: 16px;

    @include bp-mobile {
      z-index: 2 !important;
    }

    .label {
      position: absolute;
      top: calc(50% - 10px);
      left: 16px;
      transition: 0.2s ease all;
      @include text-overflow();
      line-height: 20px;
      pointer-events: none;
      color: $blue-light;
    }

    &--active {
      .label {
        top: 7px;
        font-size: em(10);
        letter-spacing: 0;
        line-height: 14px;
      }

      ::v-deep {
        input {
          top: 5px;
          margin: 0;
          font-size: em(14);
          background: transparent;
        }

        .multiselect__tags {
          border: 1px solid $blue-medium !important;
          border-radius: 4px;

          input {
            height: 24px;
            line-height: 24px;
            padding: 0;
          }
        }
      }

      .arrow {
        transform: rotate(180deg);
      }
    }

    ::v-deep {
      .multiselect__tags {
        min-height: 58px;
        padding: 16px 60px 6px 16px;
        border: 1px solid $blue-border;
        font-size: em(14);
        line-height: 20px;
        color: $blue-light;
        border-radius: 4px;
      }

      .multiselect__content-wrapper {
        top: 65px;

        @include bp-mobile {
          max-height: unset !important;
        }

        @include bp-mf-tablet {
          @include box-shadow();
        }
      }

      .multiselect__placeholder {
        font-size: em(14);
        color: $blue-light;
        margin: 0;
      }

      .multiselect__single {
        color: #152649;
        background: transparent;

        .label {
          position: absolute;
          width: 100%;
          transition: 0.2s ease all;
          @include text-overflow();
          pointer-events: none;
          color: $blue-light;
          top: -10px;
          left: 0;
          font-size: em(10);
          letter-spacing: 0;
          line-height: 14px;
        }

        .selected {
          position: relative;
          top: 8px;
          left: -5px;
        }
      }

      .multiselect__option {
        height: 55px;
        padding: 14px 16px;
        font-size: em(14);
        letter-spacing: 0;
        line-height: 27px;

        &--selected,
        &--highlight {
          background-color: $gray-marble;
          color: $dropdown-selected;
          font-weight: 500;
        }
      }
    }
  }

  .multiselect--above {
    ::v-deep.multiselect__content-wrapper {
      top: unset;
    }
  }

  .arrow {
    display: block;
    height: 40px;
    line-height: 16px;
    position: absolute;
    right: 19px;
    transform: rotate(90deg);
    top: calc(50% - 20px);
    width: 40px;

    &::after {
      color: $blue-dark;
      content: $icon-arrow-right-thin;
      font-size: 16px;
      left: calc(50% - 5px);
      top: calc(50% - 10px);
      position: absolute;
      @include icon-font-thin();
    }
  }
</style>
